<template>
    <form-row
        v-if="is_buyer_side"
        title="Order Title"
        :label_align_top="true"
    >
        <div class="flex flex-col flex-1 w-full">
            <div class="rounded-lg border border-gray-300 bg-gray-50 p-5 sm:p-6">
                <div class="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-4">
                    <div class="flex-1 space-y-3">
                        <div class="flex items-center gap-3">
                            <div class="flex-none flex items-center justify-center w-10 h-10 rounded-full bg-blue-100 text-blue-700 font-bold text-sm">
                                TE
                            </div>

                            <div>
                                <h3 class="text-lg font-semibold text-gray-warm-800 leading-tight">
                                    TitleEase
                                </h3>
                                <p class="text-sm text-gray-500">
                                    Title insurance &amp; closing services
                                </p>
                            </div>
                        </div>

                        <template v-if="has_order && order_accepted">
                            <p class="text-sm text-gray-600 leading-relaxed max-w-xl">
                                A title order has been submitted for this transaction.
                                Use the link to view order status and details in TitleEase.
                            </p>

                            <p class="text-xs text-gray-500 leading-relaxed max-w-xl">
                                If you haven&apos;t set up your TitleEase account yet, check your email
                                for instructions on how to do so.
                            </p>
                        </template>

                        <template v-else-if="has_order">
                            <p class="text-sm text-gray-600 leading-relaxed max-w-xl">
                                A title order has been submitted for this transaction.
                                An invitation to access your TitleEase order will be sent to your email
                                within 24–48 hours.
                            </p>
                        </template>

                        <template v-else>
                            <p class="text-sm text-gray-600 leading-relaxed max-w-xl">
                                Order title for this transaction through TitleEase, our trusted partner
                                for title insurance and settlement services. Property and contract details
                                from this deal will be shared securely when you submit your order.
                            </p>

                            <ul class="text-sm text-gray-600 space-y-1">
                                <li class="flex items-start gap-2">
                                    <span class="text-blue-500 mt-0.5">•</span>
                                    <span>Streamlined title ordering from within your transaction</span>
                                </li>
                                <li class="flex items-start gap-2">
                                    <span class="text-blue-500 mt-0.5">•</span>
                                    <span>Uses your existing property and closing information</span>
                                </li>
                            </ul>
                        </template>
                    </div>

                    <div class="flex-none sm:pt-1">
                        <a v-if="has_order && order_accepted"
                            :href="titleease_order_url"
                            target="_blank"
                            rel="noopener noreferrer"
                            class="inline-block border text-white uppercase font-semibold tracking-wider leading-normal text-xs px-6 py-3 bg-blue-500 border-blue-500 hover:border-blue-600 hover:bg-blue-600 cursor-pointer"
                        >
                            View Title Order
                        </a>

                        <button-blue
                            v-else-if="! has_order"
                            :disabled="read_only"
                            @click="openModal"
                        >
                            Order Title
                        </button-blue>
                    </div>
                </div>
            </div>

            <titleease-order-modal
                v-if="! has_order"
                :show="show_modal"
                :property="property"
                :order_title_route="order_title_route"
                :contract_documents_route="contract_documents_route"
                :read_only="read_only"
                @closed="show_modal = false"
                @ordered="handleOrdered"
            />
        </div>
    </form-row>
</template>

<script>
import ButtonBlue from '../../components/button-blue.vue';
import TitleeaseOrderModal from './titleease-order-modal.vue';

export default {
    components: {
        ButtonBlue,
        TitleeaseOrderModal,
    },

    props: {
        property: { default: () => ({}) },
        order_title_route: {},
        order_status_route: {},
        contract_documents_route: {},
        read_only: { default: false },
    },

    data() {
        return {
            show_modal: false,
            titleease_order_id: this.property.titleease_order_id || null,
            titleease_order_url: this.property.titleease_order_url || null,
            order_accepted: !! this.property.titleease_order_accepted,
            checking_status: false,
        }
    },

    computed: {
        is_buyer_side() {
            return !! this.property.deal?.our_purchase;
        },
        has_order() {
            return !! this.titleease_order_id;
        },
    },

    mounted() {
        this.refreshStatus();
    },

    methods: {
        openModal() {
            if (this.read_only || this.has_order) {
                return;
            }

            this.show_modal = true;
        },
        handleOrdered(response) {
            this.titleease_order_id = response.titleease_order_id
                || response.order?._id
                || null;
            this.titleease_order_url = response.titleease_order_url || null;
            this.order_accepted = !! response.order_accepted;
            this.show_modal = false;
            this.$emit('ordered', response);
        },
        refreshStatus() {
            if (! this.has_order || this.order_accepted || ! this.order_status_route || this.checking_status) {
                return;
            }

            this.checking_status = true;

            axios.get(this.order_status_route)
                .then(response => {
                    this.order_accepted = !! response.data.order_accepted;
                    this.titleease_order_url = response.data.titleease_order_url
                        || this.titleease_order_url;

                    if (response.data.closing && this.property.closing) {
                        Object.assign(this.property.closing, response.data.closing);
                    }
                }).catch(() => {
                    // Keep pending state if status cannot be refreshed.
                }).finally(() => {
                    this.checking_status = false;
                });
        },
    },
}
</script>
